import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'mykart_language';
const DEFAULT_LANGUAGE = 'pl';
const LOCALES = {
  pl: 'pl-PL',
  en: 'en-US',
  de: 'de-DE',
  sk: 'sk-SK',
  cs: 'cs-CZ',
};

// Catalogs are split per language so the entry chunk carries none of them;
// only the active language and the Polish fallback are fetched on start.
const CATALOG_LOADERS = {
  pl: () => import(/* webpackChunkName: "i18n-pl" */ './locales/pl.json'),
  en: () => import(/* webpackChunkName: "i18n-en" */ './locales/en.json'),
  de: () => import(/* webpackChunkName: "i18n-de" */ './locales/de.json'),
  sk: () => import(/* webpackChunkName: "i18n-sk" */ './locales/sk.json'),
  cs: () => import(/* webpackChunkName: "i18n-cs" */ './locales/cs.json'),
};

const CATALOGS = {};
const pendingCatalogs = {};

// Jest renders synchronously and calls translate() outside any provider;
// production builds drop this branch entirely.
if (process.env.NODE_ENV === 'test') {
  Object.assign(CATALOGS, {
    pl: require('./locales/pl.json'),
    en: require('./locales/en.json'),
    de: require('./locales/de.json'),
    sk: require('./locales/sk.json'),
    cs: require('./locales/cs.json'),
  });
}

function hasCatalogs(language) {
  return Boolean(CATALOGS[language] && CATALOGS[DEFAULT_LANGUAGE]);
}

function loadCatalog(language) {
  if (CATALOGS[language]) return Promise.resolve(CATALOGS[language]);
  if (!pendingCatalogs[language]) {
    pendingCatalogs[language] = CATALOG_LOADERS[language]()
      .then(module => {
        CATALOGS[language] = module.default || module;
        return CATALOGS[language];
      })
      .finally(() => { delete pendingCatalogs[language]; });
  }
  return pendingCatalogs[language];
}

export function loadLanguage(language) {
  return Promise.all([loadCatalog(DEFAULT_LANGUAGE), loadCatalog(language)]);
}

const LANGUAGE_OPTIONS = [
  { code: 'pl', label: 'Polski', flag: '🇵🇱' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
  { code: 'sk', label: 'Slovenčina', flag: '🇸🇰' },
  { code: 'cs', label: 'Čeština', flag: '🇨🇿' },
];

let activeLanguage = DEFAULT_LANGUAGE;

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function getInitialLanguage() {
  if (typeof window === 'undefined') {
    return DEFAULT_LANGUAGE;
  }

  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && CATALOG_LOADERS[stored]) {
    return stored;
  }

  const browserLanguage = navigator.language?.toLowerCase() || '';
  if (browserLanguage.startsWith('pl')) return 'pl';
  if (browserLanguage.startsWith('de')) return 'de';
  if (browserLanguage.startsWith('sk')) return 'sk';
  if (browserLanguage.startsWith('cs')) return 'cs';
  return 'en';
}

function resolveValue(catalog, key) {
  if (!catalog || !key) {
    return undefined;
  }

  if (hasOwn(catalog, key)) {
    return catalog[key];
  }

  return key.split('.').reduce((value, part) => {
    if (value && typeof value === 'object' && hasOwn(value, part)) {
      return value[part];
    }

    return undefined;
  }, catalog);
}

function interpolate(template, values = {}) {
  if (typeof template !== 'string') {
    return template;
  }

  return template.replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    const value = values[key];
    return value == null ? '' : String(value);
  });
}

function getCatalog(language) {
  return CATALOGS[language] || CATALOGS[DEFAULT_LANGUAGE] || {};
}

export function getRawTranslation(key, language = activeLanguage) {
  const value = resolveValue(getCatalog(language), key);
  if (value !== undefined) {
    return value;
  }

  return resolveValue(getCatalog(DEFAULT_LANGUAGE), key);
}

export function translate(key, values = {}, language = activeLanguage) {
  const value = getRawTranslation(key, language);

  if (typeof value === 'string') {
    return interpolate(value, values);
  }

  if (value == null) {
    return interpolate(key, values);
  }

  return value;
}

export function translatePlural(key, count, values = {}, language = activeLanguage) {
  const forms = getRawTranslation(key, language);

  if (!forms || typeof forms !== 'object' || Array.isArray(forms)) {
    return translate(key, { ...values, count }, language);
  }

  const category = new Intl.PluralRules(language).select(count);
  const template = forms[category] ?? forms.other ?? forms.many ?? forms.few ?? forms.one;
  return interpolate(template, { ...values, count });
}

function normalizeDateInput(value) {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  if (typeof value?.toDate === 'function') {
    return value.toDate();
  }

  if (typeof value?.seconds === 'number') {
    return new Date(value.seconds * 1000);
  }

  if (typeof value === 'number') {
    return new Date(value);
  }

  return new Date(value);
}

export function formatLocaleDate(value, options, language = activeLanguage) {
  const date = normalizeDateInput(value);
  if (!date || Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(LOCALES[language] || LOCALES[DEFAULT_LANGUAGE], options).format(date);
}

const I18nContext = createContext(null);
// Standalone game previews and tests may intentionally have no app provider.
export function useOptionalI18n() { return useContext(I18nContext); }

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);
  const [ready, setReady] = useState(() => hasCatalogs(language));

  useEffect(() => {
    if (ready) return undefined;
    let cancelled = false;
    // A failed fetch (offline, stale deploy) still renders, falling back to keys.
    loadLanguage(language).catch(() => {}).finally(() => { if (!cancelled) setReady(true); });
    return () => { cancelled = true; };
  }, [language, ready]);

  useEffect(() => {
    activeLanguage = language;
    document.documentElement.lang = language;
    window.localStorage.setItem(STORAGE_KEY, language);
  }, [language]);

  const value = useMemo(() => ({
    language,
    locale: LOCALES[language] || LOCALES[DEFAULT_LANGUAGE],
    languages: LANGUAGE_OPTIONS,
    setLanguage: nextLanguage => {
      if (!CATALOG_LOADERS[nextLanguage]) return;
      if (hasCatalogs(nextLanguage)) {
        setLanguageState(nextLanguage);
        return;
      }
      // Keep the current language on screen until the new catalog arrives.
      loadLanguage(nextLanguage).then(() => setLanguageState(nextLanguage)).catch(() => {});
    },
    t: (key, values) => translate(key, values, language),
    tp: (key, count, values) => translatePlural(key, count, values, language),
    getRaw: key => getRawTranslation(key, language),
    formatDate: (value, options) => formatLocaleDate(value, options, language),
  }), [language]);

  if (!ready) return null;

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  return context;
}
